import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, Copy, Route as RouteIcon, ChevronRight, PlayCircle } from "lucide-react";
import { toast } from "sonner";

// Decline Drive pitch video — new iPad-safe MOV with sound, uploaded by James
// on 2026-02-11. Overrides the older Onyx-narrated MP4. Served from /public/downloads.
const VIDEO_SRC = "/downloads/DeclineDrive_iPad_Safe_Text_With_Sound.mov";
const PROMO_SRC = "/downloads/decline-drive-promo-9s.mp4";

export default function PitchPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("pitch"); // 'pitch' | 'promo'
  const src = current === "pitch" ? VIDEO_SRC : PROMO_SRC;

  const copyLink = () => {
    const full = window.location.origin + src;
    navigator.clipboard
      .writeText(full)
      .then(() => toast.success("Link copied — paste into a text to send to a dealer"))
      .catch(() => toast.error("Couldn't copy — long-press the download button and share"));
  };

  return (
    <DashboardShell>
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-label text-muted-foreground">Live Pitch · Voiced Video</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            The Sixty-Second Pitch
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            Full narrated walkthrough — the exact pitch you'd give from an iPad, on paper, in one
            minute. Deep-voice narrator, 93% capture-rate stat, six-figure recovery close.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrent(current === "pitch" ? "promo" : "pitch")}
            data-testid="pitch-toggle-length-btn"
          >
            {current === "pitch" ? "Switch to 9-sec promo" : "Switch to 60-sec pitch"}
          </Button>
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="pitch-copy-link-btn">
            <Copy className="w-4 h-4 mr-1.5" strokeWidth={2} />
            Copy link
          </Button>
          <Button asChild size="sm" data-testid="pitch-download-btn">
            <a href={src} download>
              <Download className="w-4 h-4 mr-1.5" strokeWidth={2} />
              Download video
            </a>
          </Button>
        </div>
      </div>

      {/* Video stage — dark cinematic frame, mirrors the dealer-showroom feel */}
      <div
        className="relative rounded-xl overflow-hidden bg-black shadow-2xl ring-1 ring-white/5"
        data-testid="pitch-video-stage"
      >
        <video
          key={src}
          src={src}
          controls
          playsInline
          preload="auto"
          poster=""
          className="w-full max-h-[70vh] block bg-black"
          data-testid="pitch-video-el"
        />
      </div>

      {/* Speaker notes — printable one-pager for the dealer to keep */}
      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Card className="p-4 border-border md:col-span-2">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Narration Script</div>
          <div className="mt-2 text-sm leading-relaxed text-foreground/85 space-y-2">
            <p><b>[Open]</b> Service revenue walks out your drive. Every day.</p>
            <p><b>[Command Center]</b> Meet Decline Drive. Every declined repair order tracked from the moment your customer says <i>not today</i>.</p>
            <p><b>[NOT TODAY]</b> Everyone else forgets. Left on a shelf. Paying you nothing.</p>
            <p><b>[Automated Follow-Up]</b> We turn <i>not today</i> into automated follow-up — a personal text, a unique coupon, a tap to schedule.</p>
            <p><b>[The Cadence]</b> Day seven. Day fifteen. Day thirty. Zero advisor clicks.</p>
            <p><b>[Parts Pipeline]</b> Every special-order part — tracked from shelf to installed. The moment it arrives, your customer knows. No more forgotten parts.</p>
            <p><b>[Body Shop]</b> Collision customers auto-texted the day their car is ready. One less phone call. One more happy customer.</p>
            <p><b>[Real-Time Visibility]</b> Ninety-three percent capture rate. Every advisor. Every dollar. Every day.</p>
            <p><b>[The Close]</b> You'll pull back one hundred twenty-nine thousand dollars. Six figures every year. Pays for itself month one.</p>
            <p><b>[CTA]</b> Plans start at fourteen hundred ninety-five a month. Decline Drive — we drive further.</p>
          </div>
        </Card>

        <Card className="p-4 border-border">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Also Try</div>
          <button
            onClick={() => navigate("/customer-journey")}
            className="w-full text-left mt-2 p-3 rounded-md border border-border hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/5 transition-colors group"
            data-testid="pitch-goto-journey-btn"
          >
            <div className="flex items-center gap-2">
              <RouteIcon className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
              <div className="flex-1 font-display font-bold text-sm">
                Animated Customer Journey
              </div>
              <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
            </div>
            <div className="text-[0.65rem] text-muted-foreground mt-1 leading-snug">
              9-step visual timeline with pulsing animation — good for lobby TV loop.
            </div>
          </button>
          <button
            onClick={() => navigate("/how-it-works")}
            className="w-full text-left mt-2 p-3 rounded-md border border-border hover:border-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/5 transition-colors group"
            data-testid="pitch-goto-how-btn"
          >
            <div className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
              <div className="flex-1 font-display font-bold text-sm">How It Works</div>
              <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
            </div>
            <div className="text-[0.65rem] text-muted-foreground mt-1 leading-snug">
              4-step explainer with animated phone mockups.
            </div>
          </button>
        </Card>
      </div>
    </DashboardShell>
  );
}
